import { Outlet, Route } from 'react-router-dom';
import cx from 'classnames';
import {
  RiEqualizerFill,
  RiFileListFill,
  RiEdit2Fill,
  RiDashboardFill,
  RiBarChartFill,
} from 'react-icons/ri';
import { FaRobot } from 'react-icons/fa';
import importAll from 'import-all.macro';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getRoutes } from '../../utils';
import Onboarding from './Onboarding';
import Link from '../../components/Link';
import Typography from '../../components/Typography';
import { useUserSession } from '../../components/UserSession';
import {
  useOrganizationControllerGetOrganization,
  useOrganizationControllerUpdateOrganization,
  useUserControllerDeleteMe,
} from '../../api/informApi/informApiComponents';
import {
  SerializedOrganization,
  UpdateOrganizationDto,
} from '../../api/informApi/informApiSchemas';
import Toggle, { ToggleProps } from '../../components/Toggle';
import styles from './Dashboard.module.scss';
import { IconType } from 'react-icons';
import { toast } from 'react-hot-toast';
import { pick } from 'lodash';
import Card from '../../components/Card';
import Button from '../../components/Button';

const routes = getRoutes(importAll.sync('./*/index.tsx'));

type SidenavItemProps = {
  icon: IconType;
  children: string;
} & (
  | {
      type: 'link';
      beta?: boolean;
      to: string;
    }
  | {
      type: 'category';
      value: ToggleProps['value'];
      onChange: ToggleProps['onChange'];
      disabled?: boolean;
    }
);

function SidenavItem(props: SidenavItemProps) {
  const renderContent = () => (
    <>
      <props.icon className={styles.navIcon} width="1rem" />
      <Typography className={styles.navText} textWeight="bold" textType="p3">
        {props.children}
      </Typography>
    </>
  );

  switch (props.type) {
    case 'category':
      return (
        <Card
          padding="1rem"
          backgroundColor="background-3"
          className={cx(styles.navLabel, props.value && styles.enabled)}
        >
          {renderContent()}
          <Toggle
            value={props.value}
            onChange={props.onChange}
            disabled={props.disabled}
            scale="0.7rem"
          />
        </Card>
      );
    case 'link':
      return (
        <Card padding="0" as="li">
          <Link className={styles.navItem} type="navlink" to={props.to} pure>
            {renderContent()}
            {props.beta && <span className={styles.navItemBeta}>Beta</span>}
          </Link>
        </Card>
      );
    default:
      return null;
  }
}

interface SlotProps {
  refetch: () => Promise<any>;
  data: SerializedOrganization;
  isRefetching: boolean;
}
function Sidenav({ data, refetch, isRefetching }: SlotProps) {
  const { currentOrganization } = useUserSession();
  const { mutateAsync, isLoading } =
    useOrganizationControllerUpdateOrganization();
  const disableActions = isRefetching || isLoading;

  const updateOrganization = (patch: UpdateOrganizationDto) => {
    return mutateAsync({
      pathParams: { id: currentOrganization! },
      body: {
        ...pick(data, ['enableTemplates', 'enableAi', 'name']),
        ...patch,
      },
    }).then(() => refetch());
  };

  return (
    <ul className={styles.navItems}>
      <SidenavItem icon={RiDashboardFill} to="/reviews" type="link">
        Reviews
      </SidenavItem>
      <SidenavItem icon={RiBarChartFill} to="/analytics" type="link" beta>
        Analytics
      </SidenavItem>
      <li>
        <SidenavItem
          onChange={(enableAi) =>
            toast.promise(updateOrganization({ enableAi }), {
              loading: `${enableAi ? 'Enabling' : 'Disabling'} AI Responses...`,
              error: `Unable to ${
                enableAi ? 'enable' : 'disable'
              } AI Responses. Please try again later.`,
              success: `AI Responses ${enableAi ? 'enabled' : 'disabled'}!`,
            })
          }
          disabled={disableActions}
          value={data.enableAi}
          icon={FaRobot}
          type="category"
        >
          AI Responses
        </SidenavItem>
        <ul className={styles.navItems}>
          <SidenavItem icon={RiEqualizerFill} to="/ai" type="link">
            AI Rules
          </SidenavItem>
        </ul>
      </li>
      <li>
        <SidenavItem
          onChange={(enableTemplates) =>
            toast.promise(updateOrganization({ enableTemplates }), {
              loading: `${
                enableTemplates ? 'Enabling' : 'Disabling'
              } Templated Responses...`,
              error: `Unable to ${
                enableTemplates ? 'enable' : 'disable'
              } Templated Responses. Please try again later.`,
              success: `Templated Responses ${
                enableTemplates ? 'enabled' : 'disabled'
              }!`,
            })
          }
          value={data.enableTemplates}
          disabled={disableActions}
          icon={RiEdit2Fill}
          type="category"
        >
          Templated Responses
        </SidenavItem>
        <ul className={styles.navItems}>
          <SidenavItem icon={RiEqualizerFill} to="/rules" type="link">
            Rules
          </SidenavItem>
          <SidenavItem icon={RiFileListFill} to="/templates" type="link">
            Templates
          </SidenavItem>
        </ul>
      </li>
    </ul>
  );
}

function Header({ data, refetch, isRefetching }: SlotProps) {
  const { currentOrganization } = useUserSession();
  const { mutateAsync, isLoading } =
    useOrganizationControllerUpdateOrganization();
  const disableActions = isRefetching || isLoading;

  const updateOrganization = (patch: UpdateOrganizationDto) => {
    return mutateAsync({
      pathParams: { id: currentOrganization! },
      body: {
        ...pick(data, ['enableTemplates', 'enableAi', 'name']),
        ...patch,
      },
    }).then(() => refetch());
  };

  return (
    <div className={styles.header}>
      <div className={styles.headerLabel}>
        <Typography textWeight="bold" textType="p3" as="label">
          Weekly Auto-Reply
        </Typography>
        <Toggle
          onChange={(enableAutoReply) =>
            toast.promise(updateOrganization({ enableAutoReply }), {
              loading: `${
                enableAutoReply ? 'Enabling' : 'Disabling'
              } Auto Reply...`,
              error: `Unable to ${
                enableAutoReply ? 'enable' : 'disable'
              } Auto Reply. Please try again later.`,
              success: `Auto Reply ${
                enableAutoReply ? 'enabled' : 'disabled'
              }!`,
            })
          }
          disabled={disableActions}
          value={data.enableAutoReply}
          scale="0.7rem"
        />
      </div>
      <Typography textType="p4">
        Auto-replies run every Monday at 12:00PM EST
      </Typography>
    </div>
  );
}

function Footer({ data }: SlotProps) {
  const { mutateAsync, isLoading } = useUserControllerDeleteMe();
  const auth = useUserSession();
  const deleteUserPrompt = () => {
    toast(
      <div className={styles.deletePrompt}>
        <Typography as="p" textType="p2" textWeight="bold">
          Are you sure you want to delete your account?
        </Typography>
        <Typography as="p" textType="p2">
          <i>This action cannot be undone</i>
        </Typography>
        <div className={styles.deletePromptActions}>
          <Button
            variant="solid"
            buttonColor="error"
            onClick={async () => {
              await toast.promise(
                mutateAsync({}),
                {
                  loading: 'Deleting account...',
                  success: 'Account deleted.',
                  error: 'Unable to delete account. Try again later.',
                },
                {
                  id: 'delete-user',
                },
              );
              window.localStorage.clear();
              auth.setToken(null);
            }}
          >
            Delete Account
          </Button>
          <Button
            onClick={() => toast.dismiss('delete-user')}
            variant="solid"
            buttonColor="background-4"
          >
            Cancel
          </Button>
        </div>
      </div>,
      {
        duration: 15000,
        id: 'delete-user',
      },
    );
  };

  return (
    <div className={styles.footer}>
      <Typography className={styles.footerLinks} textType="p4">
        <li>
          <Link
            type="anchor"
            to="/terms"
            target="_blank"
            rel="noreferrer noopener"
          >
            Terms & Conditions
          </Link>
        </li>
        <li>
          <Link
            type="anchor"
            to="/privacy"
            target="_blank"
            rel="noreferrer noopener"
          >
            Privacy Policy
          </Link>
        </li>
        <li>
          <Link type="anchor" to={`mailto:hello@informreviews.net`}>
            Contact Us
          </Link>
        </li>
      </Typography>
      <Button
        onClick={deleteUserPrompt}
        className={styles.footerAction}
        variant="ghost"
        buttonColor="error"
        disabled={isLoading}
      >
        Delete Account
      </Button>
    </div>
  );
}

function Dashboard() {
  const { currentOrganization } = useUserSession();
  const { data, refetch, isLoading, isRefetching } =
    useOrganizationControllerGetOrganization({
      pathParams: { id: currentOrganization! },
    });

  if (isLoading || !data) return null;

  return (
    <DashboardLayout
      header={
        <Header data={data} refetch={refetch} isRefetching={isRefetching} />
      }
      nav={
        <Sidenav data={data} refetch={refetch} isRefetching={isRefetching} />
      }
      footer={
        <Footer data={data} refetch={refetch} isRefetching={isRefetching} />
      }
    >
      <Outlet />
      <Onboarding />
    </DashboardLayout>
  );
}

Dashboard.routeProps = {
  path: '/',
  isProtected: true,
  children: (
    <>
      {routes.map((route, key) => {
        const Component = route;
        return (
          <Route
            {...(route.routeProps ?? {})}
            element={<Component />}
            key={key}
          />
        );
      })}
    </>
  ),
};
export default Dashboard;
